/**
 * ChartDataLoader - 3-tier data loading (memory → IndexedDB → API)
 * Migrated from multi-strategy-signals.html
 */

import APIClient from '../api/client.js';
import CacheManager from './cacheManager.js';

class ChartDataLoader {
  constructor() {
    this.memoryCache = new Map();
  }

  async loadChartData(ticker) {
    // Tier 1: Memory cache (instant)
    if (this.memoryCache.has(ticker)) {
      console.log(`[ChartLoader] Memory cache hit: ${ticker}`);
      return this.memoryCache.get(ticker);
    }

    // Tier 2: IndexedDB cache (~1-2ms)
    const cacheKey = `chart_${ticker}`;
    const cached = await CacheManager.getIndexedDB(cacheKey);
    if (cached) {
      console.log(`[ChartLoader] IndexedDB cache hit: ${ticker}`);
      this.memoryCache.set(ticker, cached);
      return cached;
    }

    // Tier 3: API fetch (~100-500ms)
    console.log(`[ChartLoader] Fetching from API: ${ticker}`);
    const data = await APIClient.fetchChartData(ticker);

    // Store in both caches
    this.memoryCache.set(ticker, data);
    await CacheManager.setIndexedDB(cacheKey, data);

    return data;
  }

  async loadHistoricalData(ticker, period = '1y') {
    const cacheKey = `history_${ticker}_${period}`;

    // Check memory cache
    if (this.memoryCache.has(cacheKey)) {
      return this.memoryCache.get(cacheKey);
    }

    // Check IndexedDB
    const cached = await CacheManager.getIndexedDB(cacheKey);
    if (cached) {
      this.memoryCache.set(cacheKey, cached);
      return cached;
    }

    // Fetch from API
    const rawData = await APIClient.fetchHistory(ticker, period);

    // Transform data to match React component expectations
    const data = this.transformHistoricalData(rawData);

    // Cache
    this.memoryCache.set(cacheKey, data);
    await CacheManager.setIndexedDB(cacheKey, data);

    return data;
  }

  transformHistoricalData(rawData) {
    // Transform API response format to match React component expectations
    // API formats supported:
    //   1. ticker-history: { dates[], ohlcv[{open,high,low,close}], sma20[], lro[], wt1[], wt2[] }
    //   2. chart endpoint: { strategies: { id: { chart_data: { dates[], open[], high[], ... } } } }
    // Components expect: { ohlcv: [{date, open, high, low, close}], sma: {sma_20, sma_200}, ... }

    if (!rawData) {
      return null;
    }

    // Helper function to convert ISO date-time to YYYY-MM-DD format
    const toDateString = (dateStr) => {
      if (!dateStr) return null;
      return String(dateStr).split('T')[0];  // "2024-01-01T12:00:00Z" → "2024-01-01"
    };

    // FORMAT 1: ticker-history response (dates[] + ohlcv[] array of objects)
    if (rawData.dates && rawData.ohlcv && Array.isArray(rawData.ohlcv)) {
      console.log('[ChartLoader] Using ticker-history format');

      const transformed = {
        ticker: rawData.ticker,
        period: rawData.period,
        ohlcv: rawData.dates.map((date, i) => ({
          date: toDateString(date),
          open: rawData.ohlcv[i]?.open !== null ? parseFloat(rawData.ohlcv[i].open) : null,
          high: rawData.ohlcv[i]?.high !== null ? parseFloat(rawData.ohlcv[i].high) : null,
          low: rawData.ohlcv[i]?.low !== null ? parseFloat(rawData.ohlcv[i].low) : null,
          close: rawData.ohlcv[i]?.close !== null ? parseFloat(rawData.ohlcv[i].close) : null,
          volume: rawData.ohlcv[i]?.volume !== null ? parseFloat(rawData.ohlcv[i].volume) : null
        })),
        sma: {},
        macd: {},
        lro: { values: [] },
        wavetrend: { wt1: [], wt2: [] }
      };

      // Map indicators from top-level arrays
      if (rawData.sma20) {
        transformed.sma.sma_20 = rawData.dates
          .map((date, i) => ({ time: toDateString(date), value: parseFloat(rawData.sma20[i]) }))
          .filter(d => d.value !== null && !isNaN(d.value));
      }
      if (rawData.sma200) {
        transformed.sma.sma_200 = rawData.dates
          .map((date, i) => ({ time: toDateString(date), value: parseFloat(rawData.sma200[i]) }))
          .filter(d => d.value !== null && !isNaN(d.value));
      }
      if (rawData.macd) {
        transformed.macd.macd = rawData.dates
          .map((date, i) => ({ time: toDateString(date), value: parseFloat(rawData.macd[i]) }))
          .filter(d => d.value !== null && !isNaN(d.value));
      }
      if (rawData.signal) {
        transformed.macd.signal = rawData.dates
          .map((date, i) => ({ time: toDateString(date), value: parseFloat(rawData.signal[i]) }))
          .filter(d => d.value !== null && !isNaN(d.value));
      }
      if (rawData.histogram) {
        transformed.macd.histogram = rawData.dates
          .map((date, i) => ({ time: toDateString(date), value: parseFloat(rawData.histogram[i]) }))
          .filter(d => d.value !== null && !isNaN(d.value));
      }
      if (rawData.lro) {
        transformed.lro.values = rawData.dates
          .map((date, i) => ({ time: toDateString(date), value: parseFloat(rawData.lro[i]) }))
          .filter(d => d.value !== null && !isNaN(d.value));
      }
      if (rawData.wt1) {
        transformed.wavetrend.wt1 = rawData.dates
          .map((date, i) => ({ time: toDateString(date), value: parseFloat(rawData.wt1[i]) }))
          .filter(d => d.value !== null && !isNaN(d.value));
      }
      if (rawData.wt2) {
        transformed.wavetrend.wt2 = rawData.dates
          .map((date, i) => ({ time: toDateString(date), value: parseFloat(rawData.wt2[i]) }))
          .filter(d => d.value !== null && !isNaN(d.value));
      }

      return transformed;
    }

    // FORMAT 2: chart endpoint (nested strategies with chart_data)
    let chartData = null;
    if (rawData.strategies) {
      const stratKeys = Object.keys(rawData.strategies);
      for (const key of stratKeys) {
        if (rawData.strategies[key]?.chart_data?.dates) {
          chartData = rawData.strategies[key].chart_data;
          break;
        }
      }
    }

    // Fallback to top-level column format
    if (!chartData) {
      if (rawData.dates && rawData.open) {
        chartData = rawData;
      } else {
        console.error('[ChartLoader] No chart_data found in response');
        return null;
      }
    }

    if (!chartData.dates || chartData.dates.length === 0) {
      console.error('[ChartLoader] chart_data.dates is empty');
      return null;
    }

    console.log('[ChartLoader] Using chart endpoint format');

    // Transform column format (dates[], open[], high[], ...) to row format
    const transformed = {
      ticker: rawData.ticker,
      period: rawData.period,
      ohlcv: chartData.dates.map((date, i) => ({
        date: toDateString(date),
        open: parseFloat(chartData.open?.[i]) || null,
        high: parseFloat(chartData.high?.[i]) || null,
        low: parseFloat(chartData.low?.[i]) || null,
        close: parseFloat(chartData.close?.[i]) || chartData.prices?.[i] || null,
        volume: parseFloat(chartData.volume?.[i]) || null
      })),
      sma: {},
      macd: {},
      lro: { values: [] },
      wavetrend: { wt1: [], wt2: [] }
    };

    // Map indicators from chartData (column arrays)
    // SMA indicators
    if (chartData.sma20) {
      transformed.sma.sma_20 = chartData.dates
        .map((date, i) => ({
          time: toDateString(date),
          value: parseFloat(chartData.sma20[i])
        }))
        .filter(d => d.value !== null && !isNaN(d.value));
    }
    if (chartData.sma200) {
      transformed.sma.sma_200 = chartData.dates
        .map((date, i) => ({
          time: toDateString(date),
          value: parseFloat(chartData.sma200[i])
        }))
        .filter(d => d.value !== null && !isNaN(d.value));
    }

    // MACD indicators
    if (chartData.macd) {
      transformed.macd.macd = chartData.dates
        .map((date, i) => ({
          time: toDateString(date),
          value: parseFloat(chartData.macd[i])
        }))
        .filter(d => d.value !== null && !isNaN(d.value));
    }
    if (chartData.macd_signal || chartData.signal) {
      transformed.macd.signal = chartData.dates
        .map((date, i) => ({
          time: toDateString(date),
          value: parseFloat(chartData.macd_signal?.[i] || chartData.signal?.[i])
        }))
        .filter(d => d.value !== null && !isNaN(d.value));
    }
    if (chartData.histogram) {
      transformed.macd.histogram = chartData.dates
        .map((date, i) => ({
          time: toDateString(date),
          value: parseFloat(chartData.histogram[i])
        }))
        .filter(d => d.value !== null && !isNaN(d.value));
    }

    // LRO indicator
    if (chartData.lro) {
      transformed.lro.values = chartData.dates
        .map((date, i) => ({
          time: toDateString(date),
          value: parseFloat(chartData.lro[i])
        }))
        .filter(d => d.value !== null && !isNaN(d.value));
    }

    // WaveTrend indicators
    if (chartData.wt1) {
      transformed.wavetrend.wt1 = chartData.dates
        .map((date, i) => ({
          time: toDateString(date),
          value: parseFloat(chartData.wt1[i])
        }))
        .filter(d => d.value !== null && !isNaN(d.value));
    }
    if (chartData.wt2) {
      transformed.wavetrend.wt2 = chartData.dates
        .map((date, i) => ({
          time: toDateString(date),
          value: parseFloat(chartData.wt2[i])
        }))
        .filter(d => d.value !== null && !isNaN(d.value));
    }

    return transformed;
  }

  clearMemoryCache() {
    this.memoryCache.clear();
  }

  evictTicker(ticker) {
    this.memoryCache.delete(ticker);
    const periods = ['1mo', '3mo', '6mo', '1y', '3y', '5y', 'all'];
    periods.forEach(period => {
      this.memoryCache.delete(`history_${ticker}_${period}`);
    });
  }
}

export default new ChartDataLoader();
